// ============================================================================
// PROCEEDING TYPE HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { proceedingTypeService } from '../../api/services/proceedingTypeService';
import type {
  ProceedingType,
  ProceedingTypeListItem,
  CreateProceedingTypeRequest,
  UpdateProceedingTypeRequest,
  ProceedingTypeSearchRequest,
} from '../../api/types/proceedingType.types';
import type { ID } from '../../api/types';

export const useProceedingTypes = (params?: ProceedingTypeSearchRequest) => {
  const [data, setData] = useState<ProceedingTypeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchProceedingTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await proceedingTypeService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchProceedingTypes(); }, [fetchProceedingTypes]);

  return { data, loading, error, pagination, refetch: fetchProceedingTypes };
};

export const useProceedingType = (id: ID | undefined) => {
  const [data, setData] = useState<ProceedingType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchProceedingType = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await proceedingTypeService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchProceedingType(); }, [fetchProceedingType]);

  return { data, loading, error, refetch: fetchProceedingType };
};

export const useCreateProceedingType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createProceedingType = async (
    data: CreateProceedingTypeRequest,
    options?: { onSuccess?: (pt: ProceedingType) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await proceedingTypeService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createProceedingType, loading, error };
};

export const useUpdateProceedingType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateProceedingType = async (
    id: ID, data: UpdateProceedingTypeRequest,
    options?: { onSuccess?: (pt: ProceedingType) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await proceedingTypeService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateProceedingType, loading, error };
};

export const useDeleteProceedingType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteProceedingType = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await proceedingTypeService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteProceedingType, loading, error };
};
